import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Tabs, Tab, Typography, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Button, ButtonGroup, IconButton, Select, MenuItem, FormControl,
  InputLabel, Chip, Switch, Dialog,
  DialogTitle, DialogContent, DialogActions, Divider, Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalculateIcon from '@mui/icons-material/Calculate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import ArchiveIcon from '@mui/icons-material/Archive';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';
import { costingApi } from '../services/costingApi';
import { leadService } from '../services/api';
import { fmtMachineLabel } from '../utils/machineLabel';

// ─── helpers ────────────────────────────────────────────────────────────────
const fmt = (v, dec = 2) => v != null ? Number(v).toFixed(dec) : '—';
const fmtGrn = (v) => v != null ? `${fmt(v)} грн` : '—';
const NEG_NUM_MSG = "Не може бути від'ємним";

function parseOptNum(v) {
  if (v === '' || v == null) return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
}

/** Помилки числових полів калькулятора / профілю (червоне поле + блок розрахунку). */
function numericFieldErrors(form, specs) {
  const e = {};
  specs.forEach(({ key, min = 0, max = null, required = false }) => {
    const raw = form[key];
    if (raw === '' || raw == null) {
      if (required) e[key] = "Обов'язкове поле";
      return;
    }
    const n = parseOptNum(raw);
    if (!Number.isFinite(n)) {
      e[key] = 'Некоректне число';
      return;
    }
    if (n < 0 && min >= 0) {
      e[key] = NEG_NUM_MSG;
      return;
    }
    if (n < min) {
      e[key] = `Мін. ${min}`;
      return;
    }
    if (max != null && n > max) e[key] = `Макс. ${max}`;
  });
  return e;
}

const CALC_NUM_SPECS = [
  { key: 'product_weight_g', min: 0.01 },
  { key: 'cavities', min: 1 },
  { key: 'cycle_sec', min: 0.1 },
  { key: 'defect_rate', min: 0, max: 100 },
  { key: 'sprue_weight_g', min: 0 },
  { key: 'sprue_return_pct', min: 0, max: 100 },
  { key: 'dye_dosage_pct', min: 0, max: 100 },
  { key: 'sale_price', min: 0.01 },
  { key: 'target_margin', min: 0, max: 99.99 },
  { key: 'batch_size', min: 1 },
  { key: 'setup_hours', min: 0 },
  { key: 'delivery_per_unit', min: 0 },
  { key: 'other_per_unit', min: 0 },
];

const PROFILE_NUM_SPECS = [
  { key: 'cavities', min: 1 },
  { key: 'cycle_sec', min: 0.1 },
  { key: 'defect_rate', min: 0, max: 1 },
  { key: 'sprue_weight_g', min: 0 },
  { key: 'sprue_return_pct', min: 0, max: 100 },
  { key: 'machine_time_share', min: 0, max: 1 },
  { key: 'typical_batch_size', min: 1 },
  { key: 'setup_hours', min: 0 },
  { key: 'sale_price', min: 0 },
];

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// ─── inline editable cell ───────────────────────────────────────────────────
function EditCell({ value, onSave, sx = {} }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');

  const commit = () => {
    setEditing(false);
    if (String(val) !== String(value ?? '')) onSave(val);
  };

  if (editing) {
    return (
      <TextField
        size="small"
        type="number"
        value={val}
        autoFocus
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setVal(value ?? ''); setEditing(false); } }}
        sx={{ width: 110, ...sx }}
        inputProps={{ style: { padding: '4px 8px', fontSize: 13 } }}
      />
    );
  }
  return (
    <Box
      onClick={() => { setVal(value ?? ''); setEditing(true); }}
      sx={{ cursor: 'pointer', minWidth: 60, px: 0.5, py: 0.25, borderBottom: '1px dashed', borderColor: 'divider', fontSize: 13, '&:hover': { bgcolor: 'action.hover' }, ...sx }}
    >
      {value ?? <span style={{ color: '#aaa' }}>—</span>}
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Sensitivity table: "По кількості ТПА"
// ────────────────────────────────────────────────────────────────────────────
function SensitivitySection({ calcId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (!calcId) return;
    setLoading(true);
    try {
      const res = await costingApi.getMachineSensitivity(calcId);
      setData(res.data);
      setOpen(true);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: open && data ? 1.5 : 0 }}>
        <Typography variant="subtitle2" fontWeight={600}>По кількості ТПА</Typography>
        <Button size="small" variant={open && data ? 'outlined' : 'contained'} onClick={() => { if (open && data) setOpen(false); else load(); }} disabled={loading}>
          {loading ? <CircularProgress size={14} /> : open && data ? 'Сховати' : 'Показати аналіз'}
        </Button>
      </Box>
      {open && data && (
        <>
          <TableContainer sx={{ mb: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell align="right">ТПА, шт</TableCell>
                  <TableCell align="right">Собівартість</TableCell>
                  <TableCell align="right">Маржа</TableCell>
                  <TableCell align="right">Прибуток/зміна</TableCell>
                  <TableCell align="right">Прибуток/місяць</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rows.map(r => (
                  <TableRow key={r.machines} sx={{ bgcolor: r.is_current ? 'action.selected' : 'inherit' }}>
                    <TableCell align="right" sx={{ fontWeight: r.is_current ? 700 : 400 }}>{r.machines}{r.is_current ? ' ★' : ''}</TableCell>
                    <TableCell align="right">{fmtGrn(r.unit_cost)}</TableCell>
                    <TableCell align="right" sx={{ color: r.margin_pct < 0 ? 'error.main' : r.margin_pct < 15 ? 'warning.main' : 'success.main' }}>{fmt(r.margin_pct)}%</TableCell>
                    <TableCell align="right">{fmtGrn(r.profit_per_shift)}</TableCell>
                    <TableCell align="right">{fmtGrn(r.profit_per_month)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Гнізда × ТПА — прибуток за зміну, грн</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Гнізда ↓ / ТПА →</TableCell>
                  {data.grid_machine_range.map(m => <TableCell key={m} align="right" sx={{ fontSize: 11 }}>{m}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.grid.map(row => (
                  <TableRow key={row.cavities}>
                    <TableCell sx={{ fontWeight: 600 }}>{row.cavities}</TableCell>
                    {row.by_machines.map((val, i) => (
                      <TableCell key={i} align="right" sx={{ fontSize: 11, color: val >= 0 ? 'success.main' : 'error.main' }}>{val != null ? Math.round(val) : '—'}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB: Калькулятор
// ────────────────────────────────────────────────────────────────────────────
function CalculatorTab({ peripherals, packagingSchemes, factorySettings, machines = [], materials = [], dyes = [] }) {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  // 'existing' — виріб з бази; 'virtual' — лише для розрахунку (не в «Вироби»)
  const [mode, setMode] = useState('existing');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [machineId, setMachineId] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [leads, setLeads] = useState([]);
  const [saveLeadId, setSaveLeadId] = useState('');
  const [savingLead, setSavingLead] = useState(false);
  const [leadSaveMsg, setLeadSaveMsg] = useState('');

  const [form, setForm] = useState({
    name: '',
    product_weight_g: '',
    sprue_weight_g: 0,
    sprue_return_pct: 100,
    cavities: '',
    cycle_sec: '',
    defect_rate: 2,
    raw_material_id: '',
    dye_id: '',
    dye_dosage_pct: '',
    selected_peripherals: [],
    packaging_scheme_id: '',
    sale_price: '',
    batch_size: '',
    setup_hours: 2,
    factory_settings_id: '',
    delivery_per_unit: 0,
    other_per_unit: 0,
    target_margin: '',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    costingApi.getProductList()
      .then(r => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoadingProducts(false));
  }, []);

  useEffect(() => {
    leadService.getAll()
      .then(r => setLeads(Array.isArray(r.data) ? r.data : (r.data?.results || [])))
      .catch(() => setLeads([]));
  }, []);

  useEffect(() => {
    if (factorySettings.length && !form.factory_settings_id) {
      setForm(f => ({ ...f, factory_settings_id: factorySettings[0].id }));
    }
  }, [factorySettings]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
  }, [imagePreview]);

  const setF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const clearImage = () => {
    setImageFile(null);
    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const resetCalcFields = (extra = {}) => {
    setForm(f => ({
      ...f,
      name: '',
      product_weight_g: '',
      sprue_weight_g: 0,
      sprue_return_pct: 100,
      cavities: '',
      cycle_sec: '',
      defect_rate: 2,
      raw_material_id: '',
      dye_id: '',
      dye_dosage_pct: '',
      selected_peripherals: [],
      packaging_scheme_id: '',
      sale_price: '',
      batch_size: '',
      setup_hours: 2,
      delivery_per_unit: 0,
      other_per_unit: 0,
      target_margin: '',
      ...extra,
    }));
  };

  const handleModeChange = (next) => {
    if (next === mode) return;
    setMode(next);
    setSelectedProduct(null);
    setSelectedConfig(null);
    setMachineId('');
    setResult(null);
    setError('');
    clearImage();
    resetCalcFields();
  };

  const handleSelectProduct = (productId) => {
    const prod = products.find(p => p.product_id === productId);
    if (!prod) return;
    setSelectedProduct(prod);
    setSelectedConfig(null);
    setResult(null);

    const firstRaw = prod.raw_components[0];
    const firstDye = prod.dye_components[0];
    const defaultSchemeId = prod.packaging_components && prod.packaging_components.length > 0
      ? `erp_${prod.packaging_components[0].packaging_material_id}` : '';

    setForm(f => ({
      ...f,
      name: prod.name,
      product_weight_g: prod.product_weight_g || '',
      raw_material_id: firstRaw ? firstRaw.raw_material_id : '',
      dye_id: firstDye ? firstDye.dye_id : '',
      dye_dosage_pct: firstDye ? firstDye.percentage : '',
      cycle_sec: '',
      cavities: '',
      sprue_weight_g: 0,
      sprue_return_pct: 100,
      packaging_scheme_id: defaultSchemeId,
    }));
  };

  const handleSelectConfig = (configId) => {
    if (!selectedProduct) return;
    const cfg = selectedProduct.manufacturing_configs.find(c => c.config_id === configId);
    if (!cfg) return;
    setSelectedConfig(cfg);
    setResult(null);
    setForm(f => ({
      ...f,
      cycle_sec: cfg.cycle_sec || '',
      cavities: cfg.cavities || '',
      sprue_weight_g: cfg.sprue_weight_g || 0,
      sprue_return_pct: cfg.sprue_return_pct != null ? cfg.sprue_return_pct : 100,
    }));
  };

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setImageFile(file);
  };

  const togglePeripheral = (id) => {
    setForm(f => {
      const sel = f.selected_peripherals.includes(id)
        ? f.selected_peripherals.filter(x => x !== id)
        : [...f.selected_peripherals, id];
      return { ...f, selected_peripherals: sel };
    });
  };

  const fieldErrors = numericFieldErrors(form, CALC_NUM_SPECS);
  const isVirtual = mode === 'virtual';
  const paramsOpen = isVirtual || !!selectedConfig;

  const isReady = () => {
    if (!form.raw_material_id || !form.factory_settings_id) return false;
    if (!form.cycle_sec || !form.cavities || !form.product_weight_g || !form.sale_price) return false;
    if (isVirtual) return !!machineId && !!String(form.name || '').trim();
    return !!selectedConfig && !!selectedProduct;
  };

  const timerRef = useRef(null);
  const peripheralsKey = form.selected_peripherals.join(',');

  useEffect(() => {
    if (!isReady()) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { doCalculate(); }, 600);
    return () => clearTimeout(timerRef.current);
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    mode,
    selectedConfig?.config_id,
    machineId,
    form.name,
    form.raw_material_id, form.dye_id, form.dye_dosage_pct,
    form.product_weight_g, form.cavities, form.cycle_sec, form.defect_rate,
    form.sprue_weight_g, form.sprue_return_pct,
    form.sale_price, form.batch_size, form.setup_hours,
    form.packaging_scheme_id, form.factory_settings_id,
    form.delivery_per_unit, form.other_per_unit, form.target_margin,
    peripheralsKey,
  ]);

  const doCalculate = async () => {
    if (!isReady()) return;
    setLoading(true); setError('');
    try {
      const payload = {
        machine_id: isVirtual ? Number(machineId) : selectedConfig.machine_id,
        factory_settings_id: form.factory_settings_id,
        raw_material_id: form.raw_material_id,
        sale_price: Number(form.sale_price),
        cycle_sec: Number(form.cycle_sec),
        cavities: Number(form.cavities),
        product_weight_g: Number(form.product_weight_g),
        name: String(form.name || '').trim() || (selectedProduct?.name || ''),
        sprue_weight_g: Number(form.sprue_weight_g) || 0,
        sprue_return_pct: Number(form.sprue_return_pct) || 100,
        defect_rate: Number(form.defect_rate) / 100,
      };
      if (!isVirtual) {
        payload.product_id = selectedProduct.product_id;
        payload.press_form_id = selectedConfig.press_form_id;
      }
      if (form.dye_id) {
        payload.dye_id = form.dye_id;
        if (form.dye_dosage_pct) payload.dye_dosage_pct = Number(form.dye_dosage_pct);
      }
      if (form.packaging_scheme_id) {
        if (String(form.packaging_scheme_id).startsWith('erp_') && selectedProduct) {
          const matId = Number(String(form.packaging_scheme_id).replace('erp_', ''));
          const pc = selectedProduct.packaging_components.find(c => c.packaging_material_id === matId);
          if (pc) payload.packaging_cost_override = pc.cost_per_unit;
        } else if (!String(form.packaging_scheme_id).startsWith('erp_')) {
          payload.packaging_scheme_id = form.packaging_scheme_id;
        }
      }
      if (form.selected_peripherals.length) payload.peripheral_equipment_ids = form.selected_peripherals;
      if (form.batch_size) payload.typical_batch_size = Number(form.batch_size);
      if (form.setup_hours) payload.setup_hours = Number(form.setup_hours);
      if (Number(form.delivery_per_unit) > 0) payload.delivery_per_unit = Number(form.delivery_per_unit);
      if (Number(form.other_per_unit) > 0) payload.other_variable_per_unit = Number(form.other_per_unit);
      if (form.target_margin !== '') payload.target_margin_pct = Number(form.target_margin);

      const res = await costingApi.calculate(payload);
      let data = res.data;
      if (isVirtual && imageFile && data?.id) {
        try {
          const up = await costingApi.uploadCalculationImage(data.id, imageFile);
          data = { ...data, ...up.data };
          // не перезаливати на кожен авто-розрахунок
          setImageFile(null);
        } catch (_) { /* розрахунок уже збережено — фото не блокує */ }
      }
      setResult(data);
      setLeadSaveMsg('');
      if (data?.lead) setSaveLeadId(String(data.lead));
      setError('');
    } catch (e) {
      setError(e?.response?.data?.error || String(e?.response?.data?.detail || 'Помилка розрахунку').slice(0, 200));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToLead = async () => {
    if (!result?.id || !saveLeadId) return;
    setSavingLead(true);
    setLeadSaveMsg('');
    try {
      const res = await costingApi.attachCalculationToLead(result.id, Number(saveLeadId));
      setResult(prev => ({ ...prev, ...res.data }));
      const leadName = res.data?.lead_detail?.name
        || leads.find(l => String(l.id) === String(saveLeadId))?.name
        || `#${saveLeadId}`;
      setLeadSaveMsg(`Збережено в лід «${leadName}»`);
    } catch (e) {
      setLeadSaveMsg(e?.response?.data?.detail || e?.response?.data?.error || 'Не вдалося зберегти в лід');
    } finally {
      setSavingLead(false);
    }
  };

  const verdictColor = (m) => m == null ? 'default' : m < 0 ? 'error' : m < 15 ? 'warning' : 'success';
  const verdictIcon = (m) => m == null ? null : m < 0 ? <ErrorIcon fontSize="small" /> : m < 15 ? <WarningIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />;
  const verdictText = (m) => m == null ? '' : m < 0 ? 'Збиток' : m < 15 ? 'Низька маржа' : 'Прибутково';

  const rawOptions = isVirtual
    ? materials.map(m => ({ id: m.material_id, label: `${m.material_name} · ${fmt(m.effective_price)} грн/кг` }))
    : (selectedProduct?.raw_components || []).map(rc => ({
        id: rc.raw_material_id,
        label: `${rc.name} · ${rc.percentage}% · ${fmt(rc.effective_price)} грн/кг`,
      }));
  const dyeOptions = isVirtual
    ? dyes.map(d => ({ id: d.dye_id, label: `${d.dye_name} · ${fmt(d.effective_price)} грн/кг` }))
    : (selectedProduct?.dye_components || []).map(dc => ({
        id: dc.dye_id,
        label: `${dc.name} · ${dc.percentage}% · ${fmt(dc.effective_price)} грн/кг`,
      }));

  if (loadingProducts) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress /></Box>;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" color="primary" mb={1}>1. Виріб</Typography>
          <ButtonGroup size="small" sx={{ mb: 2 }} fullWidth>
            <Button
              variant={mode === 'existing' ? 'contained' : 'outlined'}
              onClick={() => handleModeChange('existing')}
            >
              З бази виробів
            </Button>
            <Button
              variant={mode === 'virtual' ? 'contained' : 'outlined'}
              onClick={() => handleModeChange('virtual')}
            >
              Новий для розрахунку
            </Button>
          </ButtonGroup>

          {mode === 'existing' ? (
            <>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Виріб *</InputLabel>
                <Select
                  value={selectedProduct?.product_id || ''}
                  label="Виріб *"
                  onChange={e => handleSelectProduct(e.target.value)}
                >
                  {products.map(p => (
                    <MenuItem key={p.product_id} value={p.product_id}>
                      {p.name}{p.article ? ` (${p.article})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedProduct && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>З ERP:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedProduct.product_weight_g > 0 && (
                      <Chip size="small" label={`Вага: ${fmt(selectedProduct.product_weight_g)} г`} />
                    )}
                    {selectedProduct.raw_components.map(rc => (
                      <Chip key={rc.raw_material_id} size="small" color="info" variant="outlined"
                        label={`${rc.name} ${rc.percentage}% · ${fmt(rc.effective_price)} грн/кг`} />
                    ))}
                    {selectedProduct.dye_components.map(dc => (
                      <Chip key={dc.dye_id} size="small" color="secondary" variant="outlined"
                        label={`${dc.name} ${dc.percentage}% · ${fmt(dc.effective_price)} грн/кг`} />
                    ))}
                    {selectedProduct.packaging_cost_per_unit > 0 && (
                      <Chip size="small" color="success" variant="outlined"
                        label={`Пакування: ${fmt(selectedProduct.packaging_cost_per_unit)} грн/шт`} />
                    )}
                  </Box>
                </Box>
              )}

              {selectedProduct && (
                <>
                  <Typography variant="subtitle2" color="primary" mb={1}>2. Прес-форма + ТПА</Typography>
                  {selectedProduct.manufacturing_configs.length === 0 ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>Немає конфігурацій для цього виробу в ERP</Alert>
                  ) : (
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Прес-форма + ТПА *</InputLabel>
                      <Select
                        value={selectedConfig?.config_id || ''}
                        label="Прес-форма + ТПА *"
                        onChange={e => handleSelectConfig(e.target.value)}
                      >
                        {selectedProduct.manufacturing_configs.map(cfg => (
                          <MenuItem key={cfg.config_id} value={cfg.config_id}>
                            {cfg.press_form_identifier} · {fmtMachineLabel({
                              machine_name: cfg.machine_name,
                              machine_identifier: cfg.machine_identifier,
                            })}
                            {cfg.cycle_sec ? ` · ${cfg.cycle_sec}с` : ''}
                            {cfg.cavities ? ` · ${cfg.cavities} гн.` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2, py: 0.75 }}>
                Розрахунок без картки у «Вироби» — назва і параметри лише для цього розрахунку.
              </Alert>
              <TextField
                label="Назва виробу *"
                size="small"
                fullWidth
                sx={{ mb: 1.5 }}
                value={form.name}
                onChange={setF('name')}
                placeholder="Напр. Кришка під запит ліда"
              />
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>ТПА *</InputLabel>
                <Select value={machineId} label="ТПА *" onChange={e => { setMachineId(e.target.value); setResult(null); }}>
                  {(machines || []).map(m => (
                    <MenuItem key={m.machine_id} value={m.machine_id}>
                      {fmtMachineLabel(m)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Button component="label" variant="outlined" size="small" startIcon={<PhotoCameraIcon />}>
                  Фото виробу
                  <input hidden accept="image/*" type="file" onChange={handleImagePick} />
                </Button>
                {imagePreview && (
                  <>
                    <Box
                      component="img"
                      src={imagePreview}
                      alt="Превʼю"
                      sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                    />
                    <Button size="small" color="inherit" onClick={clearImage}>Прибрати</Button>
                  </>
                )}
              </Box>
            </>
          )}

          {paramsOpen && (
            <>
              <Typography variant="subtitle2" color="primary" mb={1}>
                {isVirtual ? '2. Параметри виробництва' : '3. Параметри виробництва'}
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6}><TextField label="Вага виробу, г *" size="small" fullWidth type="number" value={form.product_weight_g} onChange={setF('product_weight_g')} error={!!fieldErrors.product_weight_g} helperText={fieldErrors.product_weight_g || ' '} inputProps={{ min: 0, step: 'any' }} /></Grid>
                <Grid item xs={6}><TextField label="Кількість гнізд *" size="small" fullWidth type="number" value={form.cavities} onChange={setF('cavities')} error={!!fieldErrors.cavities} helperText={fieldErrors.cavities || ' '} inputProps={{ min: 1, step: 1 }} /></Grid>
                <Grid item xs={6}><TextField label="Цикл, сек *" size="small" fullWidth type="number" value={form.cycle_sec} onChange={setF('cycle_sec')} error={!!fieldErrors.cycle_sec} helperText={fieldErrors.cycle_sec || ' '} inputProps={{ min: 0, step: 'any' }} /></Grid>
                <Grid item xs={6}><TextField label="Брак, %" size="small" fullWidth type="number" value={form.defect_rate} onChange={setF('defect_rate')} error={!!fieldErrors.defect_rate} helperText={fieldErrors.defect_rate || ' '} inputProps={{ min: 0, max: 100, step: 'any' }} /></Grid>
                <Grid item xs={6}><TextField label="Ливник на цикл, г" size="small" fullWidth type="number" value={form.sprue_weight_g} onChange={setF('sprue_weight_g')} error={!!fieldErrors.sprue_weight_g} helperText={fieldErrors.sprue_weight_g || ' '} inputProps={{ min: 0, step: 'any' }} /></Grid>
                <Grid item xs={6}><TextField label="Ливник у переробку, %" size="small" fullWidth type="number" value={form.sprue_return_pct} onChange={setF('sprue_return_pct')} error={!!fieldErrors.sprue_return_pct} helperText={fieldErrors.sprue_return_pct || ' '} inputProps={{ min: 0, max: 100, step: 'any' }} /></Grid>
              </Grid>

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" color="primary" mb={1}>
                {isVirtual ? '3. Матеріал' : '4. Матеріал'}
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>Сировина *</InputLabel>
                <Select value={form.raw_material_id} label="Сировина *" onChange={setF('raw_material_id')}>
                  {rawOptions.map(o => (
                    <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={8}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Барвник</InputLabel>
                    <Select value={form.dye_id} label="Барвник" onChange={setF('dye_id')}>
                      <MenuItem value=""><em>Без барвника</em></MenuItem>
                      {dyeOptions.map(o => (
                        <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <TextField label="Дозування, %" size="small" fullWidth type="number" value={form.dye_dosage_pct} onChange={setF('dye_dosage_pct')} disabled={!form.dye_id} error={!!fieldErrors.dye_dosage_pct} helperText={fieldErrors.dye_dosage_pct || ' '} inputProps={{ min: 0, max: 100, step: 'any' }} />
                </Grid>
              </Grid>

              {!isVirtual && selectedProduct?.packaging_cost_per_unit > 0 && (
                <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                  Пакування з ERP: {fmt(selectedProduct.packaging_cost_per_unit)} грн/шт
                  {selectedProduct.packaging_components.map(pc => (
                    <span key={pc.packaging_material_id}> · {pc.name} (в уп: {pc.quantity} шт → {fmt(pc.cost_per_unit)} грн/шт)</span>
                  ))}
                </Alert>
              )}

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" color="primary" mb={1}>
                {isVirtual ? '4. Периферія' : '5. Периферія'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                {peripherals.filter(p => p.device_type === 'per_product' && p.is_active).map(p => {
                  const sel = form.selected_peripherals.includes(p.id);
                  return (
                    <Chip
                      key={p.id}
                      label={`${p.name} (${p.avg_power_kw} кВт)`}
                      size="small"
                      color={sel ? 'primary' : 'default'}
                      variant={sel ? 'filled' : 'outlined'}
                      onClick={() => togglePeripheral(p.id)}
                      sx={{ cursor: 'pointer' }}
                    />
                  );
                })}
                {peripherals.filter(p => p.device_type === 'per_product' && p.is_active).length === 0 && (
                  <Typography variant="caption" color="text.secondary">Немає активного обладнання типу «До виробу»</Typography>
                )}
              </Box>

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" color="primary" mb={1}>
                {isVirtual ? '5. Економіка' : '6. Економіка'}
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6}><TextField label="Ціна продажу з ПДВ *" size="small" fullWidth type="number" value={form.sale_price} onChange={setF('sale_price')} error={!!fieldErrors.sale_price} helperText={fieldErrors.sale_price || ' '} inputProps={{ min: 0, step: 'any' }} /></Grid>
                <Grid item xs={6}><TextField label="Бажана маржа, %" size="small" fullWidth type="number" value={form.target_margin} onChange={setF('target_margin')} placeholder="напр. 25" error={!!fieldErrors.target_margin} helperText={fieldErrors.target_margin || ' '} inputProps={{ min: 0, step: 'any' }} /></Grid>
                <Grid item xs={6}><TextField label="Партія, шт" size="small" fullWidth type="number" value={form.batch_size} onChange={setF('batch_size')} error={!!fieldErrors.batch_size} helperText={fieldErrors.batch_size || ' '} inputProps={{ min: 1, step: 1 }} /></Grid>
                <Grid item xs={6}><TextField label="Переналагодження, год" size="small" fullWidth type="number" value={form.setup_hours} onChange={setF('setup_hours')} error={!!fieldErrors.setup_hours} helperText={fieldErrors.setup_hours || ' '} inputProps={{ min: 0, step: 'any' }} /></Grid>
                <Grid item xs={6}><TextField label="Доставка виробу, грн/шт" size="small" fullWidth type="number" value={form.delivery_per_unit} onChange={setF('delivery_per_unit')} error={!!fieldErrors.delivery_per_unit} helperText={fieldErrors.delivery_per_unit || ' '} inputProps={{ min: 0, step: 'any' }} /></Grid>
                <Grid item xs={6}><TextField label="Інші витрати, грн/шт" size="small" fullWidth type="number" value={form.other_per_unit} onChange={setF('other_per_unit')} error={!!fieldErrors.other_per_unit} helperText={fieldErrors.other_per_unit || ' '} inputProps={{ min: 0, step: 'any' }} /></Grid>
                {form.selected_peripherals.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Прогрів: авто-розраховується з периферії (таб «Периферія» → «Прогрів, год»)
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <InputLabel>Витрати заводу (місяць) *</InputLabel>
                <Select value={form.factory_settings_id} label="Витрати заводу (місяць) *" onChange={setF('factory_settings_id')}>
                  {factorySettings.map(fs => (
                    <MenuItem key={fs.id} value={fs.id}>
                      {new Date(fs.month).toLocaleDateString('uk', { year: 'numeric', month: 'long' })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption">Перерахунок…</Typography>
                </Box>
              )}
            </>
          )}

          {mode === 'existing' && !selectedProduct && (
            <Alert severity="info" sx={{ mt: 1 }}>Оберіть виріб зі списку або створіть новий для розрахунку</Alert>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        {result ? (
          <Box>
            <Paper sx={{ p: 2.5, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" fontWeight={700}>Результат</Typography>
                {result.product == null && (
                  <Chip size="small" variant="outlined" label="Віртуальний виріб" />
                )}
                {result.lead && (
                  <Chip
                    size="small"
                    color="info"
                    variant="outlined"
                    label={result.lead_detail?.name ? `Лід: ${result.lead_detail.name}` : `Лід #${result.lead}`}
                  />
                )}
                <Chip
                  icon={verdictIcon(result.margin_pct)}
                  label={`${verdictText(result.margin_pct)} · ${fmt(result.margin_pct)}%`}
                  color={verdictColor(result.margin_pct)}
                  size="small"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
                  <InputLabel>Зберегти в лід</InputLabel>
                  <Select
                    value={saveLeadId}
                    label="Зберегти в лід"
                    onChange={(e) => { setSaveLeadId(e.target.value); setLeadSaveMsg(''); }}
                  >
                    <MenuItem value=""><em>— оберіть лід —</em></MenuItem>
                    {leads.map(l => (
                      <MenuItem key={l.id} value={l.id}>
                        {l.name}{l.contact_person ? ` · ${l.contact_person}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={savingLead ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={handleSaveToLead}
                  disabled={savingLead || !saveLeadId || !result.id}
                >
                  Зберегти в лід
                </Button>
              </Box>
              {leadSaveMsg && (
                <Alert
                  severity={leadSaveMsg.startsWith('Збережено') ? 'success' : 'error'}
                  sx={{ mb: 2, py: 0.5 }}
                  onClose={() => setLeadSaveMsg('')}
                >
                  {leadSaveMsg}
                </Alert>
              )}
              {(result.image || imagePreview) && (
                <Box
                  component="img"
                  src={result.image || imagePreview}
                  alt={result.name || 'Виріб'}
                  sx={{ width: '100%', maxHeight: 180, objectFit: 'contain', mb: 2, borderRadius: 1, bgcolor: 'action.hover' }}
                />
              )}
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <ResultRow label="Ціна без ПДВ" value={fmtGrn(result.sale_price_excl_vat)} highlight />
                    {result.recommended_price_excl_vat != null && (
                      <ResultRow label="Рекомендована ціна (без ПДВ)" value={fmtGrn(result.recommended_price_excl_vat)} color="primary.main" />
                    )}
                    {result.recommended_price_with_vat != null && (
                      <ResultRow label="Рекомендована ціна (з ПДВ)" value={fmtGrn(result.recommended_price_with_vat)} color="primary.main" />
                    )}
                    <ResultRow label="Собівартість" value={fmtGrn(result.unit_cost)} highlight />
                    <ResultRow label="Маржа" value={`${fmt(result.margin_pct)}%`} color={result.margin_pct < 0 ? 'error.main' : result.margin_pct < 15 ? 'warning.main' : 'success.main'} />
                    <ResultRow label="" value="" />
                    <ResultRow label="— Сировина" value={fmtGrn(result.cost_raw_material)} />
                    <ResultRow label="— Барвник" value={fmtGrn(result.cost_dye)} />
                    <ResultRow label="— Е/е ТПА" value={fmtGrn(result.cost_electricity_machine)} />
                    <ResultRow label="— Е/е периферія" value={fmtGrn(result.cost_electricity_peripheral)} />
                    <ResultRow label="— Пакування" value={fmtGrn(result.cost_packaging)} />
                    <ResultRow label="— Доставка" value={fmtGrn(result.cost_delivery)} />
                    <ResultRow label="— Оператор" value={fmtGrn(result.cost_operator)} />
                    <ResultRow label="— Накладні" value={fmtGrn(result.cost_overhead)} />
                    <ResultRow label="— Амортизація ТПА" value={fmtGrn(result.cost_machine_depreciation)} />
                    <ResultRow label="— Переналагодження" value={fmtGrn(result.cost_setup)} />
                    <ResultRow label="— Інші (прогрів+ін.)" value={fmtGrn(result.cost_other)} />
                    <ResultRow label="" value="" />
                    <ResultRow label="Прибуток/шт" value={fmtGrn(result.profit_per_unit)} color={result.profit_per_unit >= 0 ? 'success.main' : 'error.main'} />
                    <ResultRow label="Прибуток за зміну (12 год)" value={fmtGrn(result.profit_per_shift)} color={result.profit_per_shift >= 0 ? 'success.main' : 'error.main'} />
                    <ResultRow label="Прибуток за місяць" value={fmtGrn(result.profit_per_month)} color={result.profit_per_month >= 0 ? 'success.main' : 'error.main'} />
                    <ResultRow label="" value="" />
                    <ResultRow label="Вихід, шт/год" value={fmt(result.good_pcs_per_hour)} />
                    <ResultRow label="Матеріал на виріб, г" value={fmt(result.material_per_unit_g)} />
                    <ResultRow label="Маржинальний дохід/шт" value={fmtGrn(result.contribution_margin_per_unit)} />
                    <ResultRow label="Маржинальний дохід/год" value={fmtGrn(result.contribution_margin_per_hour)} />
                    <ResultRow label="Мін. ціна (змінні витрати)" value={fmtGrn(result.min_price_variable_only)} />
                    <ResultRow label="Беззбитковість (повна)" value={fmtGrn(result.breakeven_price)} />
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            <SensitivitySection calcId={result.id} />
          </Box>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
            <Box>
              <CalculateIcon sx={{ fontSize: 56, opacity: 0.2, mb: 1 }} />
              <Typography>
                {isVirtual
                  ? 'Вкажіть назву, ТПА і параметри → «Розрахувати»'
                  : 'Оберіть виріб → прес-форму+ТПА → введіть ціну → «Розрахувати»'}
              </Typography>
            </Box>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
}

function ResultRow({ label, value, highlight, color }) {
  if (!label && !value) return <TableRow><TableCell colSpan={2} sx={{ py: 0.3 }} /></TableRow>;
  return (
    <TableRow sx={highlight ? { bgcolor: 'action.selected' } : {}}>
      <TableCell sx={{ py: 0.5, fontSize: 13, color: 'text.secondary' }}>{label}</TableCell>
      <TableCell sx={{ py: 0.5, fontSize: 13, fontWeight: highlight ? 700 : 400, textAlign: 'right', color: color || 'text.primary' }}>{value}</TableCell>
    </TableRow>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB: ТПА
// ────────────────────────────────────────────────────────────────────────────
function MachinesTab({ machines, onRefresh }) {
  const save = async (machineId, field, value) => {
    try {
      const row = machines.find((m) => m.machine_id === machineId) || {};
      await costingApi.upsertMachineProfile({
        machine_id: machineId,
        power_kw: row.power_kw || 0,
        asset_value: row.asset_value || null,
        depreciation_years: row.depreciation_years || 5,
        is_working: row.is_working !== undefined ? row.is_working : true,
        [field]: value,
      });
      await onRefresh();
    } catch (e) { console.error(e); }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        Клацніть значення щоб редагувати (Enter — зберегти). Позначте «Працює» для машин що реально в роботі.
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell>Назва ТПА</TableCell>
              <TableCell align="center">Працює</TableCell>
              <TableCell align="right">Споживання, кВт</TableCell>
              <TableCell align="right">Балансова вартість, грн</TableCell>
              <TableCell align="right">Термін амортизації, р.</TableCell>
              <TableCell align="right">Амортизація/міс, грн</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {machines.map((m) => (
              <TableRow key={m.machine_id} sx={{ bgcolor: m.is_working ? 'inherit' : 'action.disabledBackground' }}>
                <TableCell sx={{ fontSize: 13 }}>{fmtMachineLabel(m)}</TableCell>
                <TableCell align="center">
                  <Switch size="small" checked={!!m.is_working} onChange={(e) => save(m.machine_id, 'is_working', e.target.checked)} color="success" />
                </TableCell>
                <TableCell align="right">
                  <EditCell value={m.power_kw} onSave={(v) => save(m.machine_id, 'power_kw', Number(v))} />
                </TableCell>
                <TableCell align="right">
                  <EditCell value={m.asset_value} onSave={(v) => save(m.machine_id, 'asset_value', v || null)} />
                </TableCell>
                <TableCell align="right">
                  <EditCell value={m.depreciation_years} onSave={(v) => save(m.machine_id, 'depreciation_years', Number(v))} />
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13, color: 'text.secondary' }}>
                  {m.depreciation_per_month ? fmt(m.depreciation_per_month) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB: Матеріали
// ────────────────────────────────────────────────────────────────────────────
function MaterialsTab({ materials, dyes, onRefresh }) {
  const [subtab, setSubtab] = useState(0);

  const saveMaterial = async (materialId, delivery_pct) => {
    try { await costingApi.upsertMaterialDelivery({ material_id: materialId, delivery_pct: Number(delivery_pct) / 100 }); await onRefresh(); }
    catch (e) { console.error(e); }
  };

  const saveDye = async (dyeId, delivery_pct) => {
    try { await costingApi.upsertDyeDelivery({ dye_id: dyeId, delivery_pct: Number(delivery_pct) / 100 }); await onRefresh(); }
    catch (e) { console.error(e); }
  };

  const MaterialTable = ({ rows, onSaveDelivery }) => (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell>Назва</TableCell>
            <TableCell>Постачальник</TableCell>
            <TableCell align="right">Ціна з ПДВ, грн/кг</TableCell>
            <TableCell align="right">Доставка, %</TableCell>
            <TableCell align="right">Ціна в розрахунок, грн/кг</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((m) => (
            <TableRow key={m.material_id || m.dye_id}>
              <TableCell sx={{ fontSize: 13 }}>{m.material_name || m.dye_name}</TableCell>
              <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{m.supplier || '—'}</TableCell>
              <TableCell align="right" sx={{ fontSize: 13 }}>{fmt(m.price_per_kg)}</TableCell>
              <TableCell align="right">
                <EditCell value={fmt(m.delivery_pct * 100, 1)} onSave={(v) => onSaveDelivery(m.material_id || m.dye_id, v)} />
              </TableCell>
              <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600 }}>{fmt(m.effective_price)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Tabs value={subtab} onChange={(_, v) => setSubtab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Сировина" />
        <Tab label="Барвники" />
      </Tabs>
      {subtab === 0 && <MaterialTable rows={materials} onSaveDelivery={saveMaterial} />}
      {subtab === 1 && <MaterialTable rows={dyes} onSaveDelivery={saveDye} />}
      <Typography variant="caption" color="text.secondary" mt={1} display="block">
        * Ціни беруться з ERP. Клацніть «Доставка» щоб змінити %.
      </Typography>
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB: Периферія
// ────────────────────────────────────────────────────────────────────────────
function PeripheralTab({ peripherals, onRefresh }) {
  const [dialog, setDialog] = useState(null);
  const empty = { name: '', device_type: 'per_product', rated_power_kw: '', duty_cycle_pct: 1, preheat_hours: 0, asset_value: '', depreciation_years: 5, is_active: true, notes: '' };
  const typeLabels = { per_product: 'До виробу', general: 'Загальна', ignore: 'Не враховувати' };

  const save = async () => {
    try {
      if (dialog.id) { await costingApi.updatePeripheral(dialog.id, dialog); }
      else { await costingApi.createPeripheral(dialog); }
      await onRefresh(); setDialog(null);
    } catch (e) { alert(e?.response?.data?.detail || 'Помилка'); }
  };

  const del = async (id) => {
    if (!window.confirm('Видалити?')) return;
    try { await costingApi.deletePeripheral(id); await onRefresh(); } catch (e) { alert('Помилка'); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => setDialog({ ...empty })}>Додати</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell>Назва</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell align="right">Потужність, кВт</TableCell>
              <TableCell align="right">Коеф. роботи</TableCell>
              <TableCell align="right">Серед. споживання, кВт</TableCell>
              <TableCell align="right">Прогрів, год</TableCell>
              <TableCell align="right">Прогрів, кВт·год</TableCell>
              <TableCell align="center">Активне</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {peripherals.map((p) => (
              <TableRow key={p.id}>
                <TableCell sx={{ fontSize: 13 }}>{p.name}</TableCell>
                <TableCell sx={{ fontSize: 13 }}><Chip label={typeLabels[p.device_type] || p.device_type} size="small" /></TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>{fmt(p.rated_power_kw)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>{fmt(p.duty_cycle_pct, 2)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600 }}>{fmt(p.avg_power_kw)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13, color: p.preheat_hours > 0 ? 'warning.main' : 'text.disabled' }}>{p.preheat_hours > 0 ? fmt(p.preheat_hours) : '—'}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13, fontWeight: p.preheat_hours > 0 ? 600 : 400, color: p.preheat_hours > 0 ? 'warning.main' : 'text.disabled' }}>{p.preheat_hours > 0 ? fmt(p.avg_power_kw * p.preheat_hours) : '—'}</TableCell>
                <TableCell align="center">
                  <Switch size="small" checked={p.is_active} onChange={async (e) => { await costingApi.updatePeripheral(p.id, { is_active: e.target.checked }); onRefresh(); }} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => setDialog({ ...p })}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => del(p.id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {dialog && (
        <Dialog open onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{dialog.id ? 'Редагувати' : 'Додати'} обладнання</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={1.5} sx={{ mt: 0 }}>
              <Grid item xs={12}><TextField label="Назва" size="small" fullWidth value={dialog.name} onChange={(e) => setDialog((d) => ({ ...d, name: e.target.value }))} /></Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Тип</InputLabel>
                  <Select value={dialog.device_type} label="Тип" onChange={(e) => setDialog((d) => ({ ...d, device_type: e.target.value }))}>
                    <MenuItem value="per_product">До виробу</MenuItem>
                    <MenuItem value="general">Загальна</MenuItem>
                    <MenuItem value="ignore">Не враховувати</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}><TextField label="Потужність, кВт" size="small" fullWidth type="number" value={dialog.rated_power_kw} onChange={(e) => setDialog((d) => ({ ...d, rated_power_kw: e.target.value }))} /></Grid>
              <Grid item xs={6}><TextField label="Коефіцієнт роботи (0–1)" size="small" fullWidth type="number" value={dialog.duty_cycle_pct} onChange={(e) => setDialog((d) => ({ ...d, duty_cycle_pct: e.target.value }))} /></Grid>
              <Grid item xs={6}><TextField label="Прогрів перед партією, год" size="small" fullWidth type="number" value={dialog.preheat_hours ?? 0} onChange={(e) => setDialog((d) => ({ ...d, preheat_hours: e.target.value }))} helperText="Авто-розраховується в калькуляторі" /></Grid>
              <Grid item xs={6}><TextField label="Балансова вартість, грн" size="small" fullWidth type="number" value={dialog.asset_value || ''} onChange={(e) => setDialog((d) => ({ ...d, asset_value: e.target.value }))} /></Grid>
              <Grid item xs={6}><TextField label="Термін амортизації, р." size="small" fullWidth type="number" value={dialog.depreciation_years} onChange={(e) => setDialog((d) => ({ ...d, depreciation_years: e.target.value }))} /></Grid>
              <Grid item xs={12}><TextField label="Коментар" size="small" fullWidth multiline rows={2} value={dialog.notes || ''} onChange={(e) => setDialog((d) => ({ ...d, notes: e.target.value }))} /></Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialog(null)}>Скасувати</Button>
            <Button variant="contained" onClick={save}>Зберегти</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB: Пакування
// ────────────────────────────────────────────────────────────────────────────
function PackagingTab({ packagingMaterials, packagingSchemes, allProducts, onRefresh }) {
  const [subTab, setSubTab] = useState(0);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null);
  const emptyScheme = { name: '', units_per_pack: '', units_per_box: '', units_per_pallet: '', pack_price: 0, box_price: 0, label_price: 0, pallet_price: 0, labor_price: 0, is_active: true, notes: '' };

  const filtered = packagingMaterials.filter(m =>
    !search || m.name.toLowerCase().includes(search.toLowerCase()) || (m.article || '').toLowerCase().includes(search.toLowerCase())
  );

  const saveScheme = async () => {
    try {
      if (dialog.id) { await costingApi.updatePackagingScheme(dialog.id, dialog); }
      else { await costingApi.createPackagingScheme(dialog); }
      await onRefresh(); setDialog(null);
    } catch (e) { alert(e?.response?.data?.detail || 'Помилка'); }
  };

  return (
    <Box>
      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="По виробах" />
        <Tab label="Всі матеріали ERP" />
        <Tab label="Схеми пакування" />
      </Tabs>

      {subTab === 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Пакування виробів з ERP — синхронізується автоматично при кожному завантаженні сторінки. Змінити можна у картці виробу (вкладка Пакування).
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Виріб</TableCell>
                  <TableCell>Пакувальний матеріал</TableCell>
                  <TableCell align="right">В упаковці, шт</TableCell>
                  <TableCell align="right">Ціна упак., грн</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Вартість/шт, грн</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(allProducts || []).filter(p => p.packaging_components && p.packaging_components.length > 0).length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>Немає виробів з пакуванням</TableCell></TableRow>
                )}
                {(allProducts || []).filter(p => p.packaging_components && p.packaging_components.length > 0).map(p =>
                  p.packaging_components.map((pc, i) => (
                    <TableRow key={`${p.product_id}-${pc.packaging_material_id}`} hover>
                      {i === 0 && (
                        <TableCell rowSpan={p.packaging_components.length} sx={{ fontSize: 13, fontWeight: 600, verticalAlign: 'top', borderRight: '1px solid', borderColor: 'divider' }}>
                          {p.name}
                          {p.article && <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.5, fontSize: 12 }}> ({p.article})</Box>}
                        </TableCell>
                      )}
                      <TableCell sx={{ fontSize: 13 }}>{pc.name}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13 }}>{pc.quantity}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, color: 'text.secondary' }}>{pc.unit_price != null ? `${fmt(pc.unit_price)}` : '—'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600, color: pc.cost_per_unit > 0 ? 'success.main' : 'text.secondary' }}>
                        {pc.cost_per_unit != null ? `${fmt(pc.cost_per_unit)}` : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {subTab === 1 && (
        <Box>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Пакувальні матеріали зі складу ERP. Ціни редагуються у розділі Склад → Упаковка.
          </Typography>
          <TextField
            size="small" placeholder="Пошук за назвою або артикулом…" value={search}
            onChange={e => setSearch(e.target.value)} sx={{ mb: 1.5, width: 320 }}
          />
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Назва</TableCell>
                  <TableCell>Артикул</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Ціна, грн/шт</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 4 }}>Немає матеріалів</TableCell></TableRow>
                )}
                {filtered.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell sx={{ fontSize: 13 }}>{m.name}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{m.article || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{m.type || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, fontWeight: m.unit_price != null ? 600 : 400, color: m.unit_price == null ? 'text.secondary' : 'inherit' }}>
                      {m.unit_price != null ? `${fmt(m.unit_price)} грн` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {subTab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
            <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => setDialog({ ...emptyScheme })}>Додати схему</Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Назва</TableCell>
                  <TableCell align="right">Шт/пакет</TableCell>
                  <TableCell align="right">Шт/коробка</TableCell>
                  <TableCell align="right">Пакет, грн</TableCell>
                  <TableCell align="right">Коробка, грн</TableCell>
                  <TableCell align="right">Наліпка, грн</TableCell>
                  <TableCell align="right">Праця, грн</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Разом/шт</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {packagingSchemes.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ color: 'text.secondary', py: 4 }}>Немає схем пакування</TableCell></TableRow>
                )}
                {packagingSchemes.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell sx={{ fontSize: 13 }}>{s.name}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>{s.units_per_pack || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>{s.units_per_box || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>{fmt(s.pack_price)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>{fmt(s.box_price)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>{fmt(s.label_price)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13 }}>{fmt(s.labor_price)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700 }}>{fmt(s.cost_per_unit)}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => setDialog({ ...s })}><EditIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {dialog && (
        <Dialog open onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{dialog.id ? 'Редагувати' : 'Додати'} схему пакування</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={1.5} sx={{ mt: 0 }}>
              <Grid item xs={12}><TextField label="Назва" size="small" fullWidth value={dialog.name} onChange={(e) => setDialog((d) => ({ ...d, name: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="Шт у пакеті" size="small" fullWidth type="number" value={dialog.units_per_pack || ''} onChange={(e) => setDialog((d) => ({ ...d, units_per_pack: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="Шт у коробці" size="small" fullWidth type="number" value={dialog.units_per_box || ''} onChange={(e) => setDialog((d) => ({ ...d, units_per_box: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="Шт на палеті" size="small" fullWidth type="number" value={dialog.units_per_pallet || ''} onChange={(e) => setDialog((d) => ({ ...d, units_per_pallet: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="Пакет, грн/пакет" size="small" fullWidth type="number" value={dialog.pack_price} onChange={(e) => setDialog((d) => ({ ...d, pack_price: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="Коробка, грн/кор" size="small" fullWidth type="number" value={dialog.box_price} onChange={(e) => setDialog((d) => ({ ...d, box_price: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="Наліпка, грн/шт" size="small" fullWidth type="number" value={dialog.label_price} onChange={(e) => setDialog((d) => ({ ...d, label_price: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="Палета, грн/пал" size="small" fullWidth type="number" value={dialog.pallet_price} onChange={(e) => setDialog((d) => ({ ...d, pallet_price: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="Праця, грн/кор" size="small" fullWidth type="number" value={dialog.labor_price} onChange={(e) => setDialog((d) => ({ ...d, labor_price: e.target.value }))} /></Grid>
              <Grid item xs={12}><TextField label="Коментар" size="small" fullWidth multiline rows={2} value={dialog.notes || ''} onChange={(e) => setDialog((d) => ({ ...d, notes: e.target.value }))} /></Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialog(null)}>Скасувати</Button>
            <Button variant="contained" onClick={saveScheme}>Зберегти</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB: Витрати заводу
// ────────────────────────────────────────────────────────────────────────────
function FactorySettingsTab({ factorySettings, onRefresh }) {
  const [dialog, setDialog] = useState(null);
  const empty = {
    month: new Date().toISOString().slice(0, 7) + '-01',
    electricity_rate: '', electricity_bill_grn: '', electricity_kwh_actual: '',
    hours_per_month: 730, machine_loading_pct: 0.85,
    operators_per_machine: 1, operator_monthly_cost: '',
    salary_admin: 0, salary_workshop: 0, salary_packers: 0,
    rent: 0, other_costs: 0, repairs_spare_parts: 0,
    usd_rate: 43, notes: '',
  };

  const save = async () => {
    try {
      if (dialog.id) { await costingApi.updateFactorySettings(dialog.id, dialog); }
      else { await costingApi.createFactorySettings(dialog); }
      await onRefresh(); setDialog(null);
    } catch (e) { alert(JSON.stringify(e?.response?.data) || 'Помилка'); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => setDialog({ ...empty })}>Новий місяць</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell>Місяць</TableCell>
              <TableCell align="right">Тариф е/е</TableCell>
              <TableCell align="right">Завантаж. ТПА</TableCell>
              <TableCell align="right">Оператор/міс</TableCell>
              <TableCell align="right">Загальні витрати/міс</TableCell>
              <TableCell align="right">USD</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {factorySettings.map((fs) => (
              <TableRow key={fs.id}>
                <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>
                  {new Date(fs.month).toLocaleDateString('uk', { year: 'numeric', month: 'long' })}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>{fmt(fs.effective_electricity_rate)} грн/кВт</TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>{fmt(fs.machine_loading_pct * 100, 0)}%</TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>{fmtGrn(fs.operator_monthly_cost)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>{fmtGrn(fs.total_overhead_per_month)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 13 }}>{fs.usd_rate || '—'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => setDialog({ ...fs })}><EditIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {dialog && (
        <Dialog open onClose={() => setDialog(null)} maxWidth="md" fullWidth>
          <DialogTitle>{dialog.id ? 'Редагувати' : 'Новий місяць'} — витрати заводу</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Grid container spacing={1.5} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <TextField label="Місяць" size="small" fullWidth type="date" value={dialog.month?.slice(0, 10) || ''} onChange={(e) => setDialog((d) => ({ ...d, month: e.target.value }))} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12}><Typography variant="subtitle2" color="primary">Електроенергія</Typography></Grid>
              <Grid item xs={4}><TextField label="Тариф договору, грн/кВт·год" size="small" fullWidth type="number" value={dialog.electricity_rate} onChange={(e) => setDialog((d) => ({ ...d, electricity_rate: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="Рахунок за місяць, грн" size="small" fullWidth type="number" value={dialog.electricity_bill_grn || ''} onChange={(e) => setDialog((d) => ({ ...d, electricity_bill_grn: e.target.value || null }))} /></Grid>
              <Grid item xs={4}><TextField label="Спожито кВт·год" size="small" fullWidth type="number" value={dialog.electricity_kwh_actual || ''} onChange={(e) => setDialog((d) => ({ ...d, electricity_kwh_actual: e.target.value || null }))} /></Grid>
              <Grid item xs={12}><Typography variant="subtitle2" color="primary">Режим роботи</Typography></Grid>
              <Grid item xs={4}><TextField label="Годин на місяць" size="small" fullWidth type="number" value={dialog.hours_per_month} onChange={(e) => setDialog((d) => ({ ...d, hours_per_month: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="Завантаження ТПА (0–1)" size="small" fullWidth type="number" value={dialog.machine_loading_pct} onChange={(e) => setDialog((d) => ({ ...d, machine_loading_pct: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="Курс USD, грн/$" size="small" fullWidth type="number" value={dialog.usd_rate || ''} onChange={(e) => setDialog((d) => ({ ...d, usd_rate: e.target.value }))} /></Grid>
              <Grid item xs={12}><Typography variant="subtitle2" color="primary">Оператори</Typography></Grid>
              <Grid item xs={4}><TextField label="Операторів на 1 ТПА" size="small" fullWidth type="number" value={dialog.operators_per_machine} onChange={(e) => setDialog((d) => ({ ...d, operators_per_machine: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="Вартість 1 оператора/міс, грн" size="small" fullWidth type="number" value={dialog.operator_monthly_cost} onChange={(e) => setDialog((d) => ({ ...d, operator_monthly_cost: e.target.value }))} /></Grid>
              <Grid item xs={12}><Typography variant="subtitle2" color="primary">Витрати заводу за місяць</Typography></Grid>
              <Grid item xs={4}><TextField label="ФОП адміністрація" size="small" fullWidth type="number" value={dialog.salary_admin} onChange={(e) => setDialog((d) => ({ ...d, salary_admin: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="ФОП цех (без операторів)" size="small" fullWidth type="number" value={dialog.salary_workshop} onChange={(e) => setDialog((d) => ({ ...d, salary_workshop: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="ФОП пакувальники + водії" size="small" fullWidth type="number" value={dialog.salary_packers} onChange={(e) => setDialog((d) => ({ ...d, salary_packers: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="Оренда + комунальні" size="small" fullWidth type="number" value={dialog.rent} onChange={(e) => setDialog((d) => ({ ...d, rent: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="Інші загальні витрати" size="small" fullWidth type="number" value={dialog.other_costs} onChange={(e) => setDialog((d) => ({ ...d, other_costs: e.target.value }))} /></Grid>
              <Grid item xs={4}><TextField label="Ремонти та запчастини ТПА" size="small" fullWidth type="number" value={dialog.repairs_spare_parts} onChange={(e) => setDialog((d) => ({ ...d, repairs_spare_parts: e.target.value }))} /></Grid>
              <Grid item xs={12}><TextField label="Коментар" size="small" fullWidth multiline rows={2} value={dialog.notes || ''} onChange={(e) => setDialog((d) => ({ ...d, notes: e.target.value }))} /></Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialog(null)}>Скасувати</Button>
            <Button variant="contained" onClick={save}>Зберегти</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TAB: Архів
// ────────────────────────────────────────────────────────────────────────────
function ArchiveTab({ calculations, onRefresh }) {
  const verdictColor = (m) => m == null ? 'default' : m < 0 ? 'error' : m < 15 ? 'warning' : 'success';

  const del = async (id) => {
    if (!window.confirm('Видалити розрахунок?')) return;
    try { await costingApi.deleteCalculation(id); await onRefresh(); } catch (e) { alert('Помилка'); }
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell>Назва / Дата</TableCell>
            <TableCell>ТПА</TableCell>
            <TableCell>Сировина</TableCell>
            <TableCell align="right">Ціна, грн</TableCell>
            <TableCell align="right">Собівартість</TableCell>
            <TableCell align="right">Маржа</TableCell>
            <TableCell align="center">Активний</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {calculations.map((c) => (
            <TableRow key={c.id} sx={c.is_active ? { bgcolor: 'action.selected' } : {}}>
              <TableCell sx={{ fontSize: 13 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant="body2" fontWeight={c.is_active ? 700 : 400}>{c.name || c.product_detail?.name || `#${c.id}`}</Typography>
                  {!c.product && !c.product_detail && (
                    <Chip size="small" variant="outlined" label="віртуальний" sx={{ height: 20, fontSize: 10 }} />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">{new Date(c.created_at).toLocaleDateString('uk')}</Typography>
              </TableCell>
              <TableCell sx={{ fontSize: 13 }}>{c.machine_detail?.name}</TableCell>
              <TableCell sx={{ fontSize: 13 }}>{c.raw_material_detail?.name}</TableCell>
              <TableCell align="right" sx={{ fontSize: 13 }}>{fmt(c.sale_price)}</TableCell>
              <TableCell align="right" sx={{ fontSize: 13 }}>{fmt(c.unit_cost)}</TableCell>
              <TableCell align="right">
                <Chip label={`${fmt(c.margin_pct)}%`} size="small" color={verdictColor(c.margin_pct)} />
              </TableCell>
              <TableCell align="center">
                {c.is_active ? <CheckCircleIcon color="success" fontSize="small" /> : null}
              </TableCell>
              <TableCell>
                <IconButton size="small" onClick={() => del(c.id)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!calculations.length && (
            <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>Немає збережених розрахунків</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// STANDARD COST TAB — профілі + авто-розрахунок + поточні собівартості
// ────────────────────────────────────────────────────────────────────────────
function StandardCostTab({ allProducts, machines, factorySettings, onRefresh }) {
  const [subTab, setSubTab] = useState(0);
  const [profiles, setProfiles] = useState([]);
  const [currentCosts, setCurrentCosts] = useState([]);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [editProfile, setEditProfile] = useState(null); // null | 'new' | {id,...}
  const [profileForm, setProfileForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');

  const loadData = useCallback(async () => {
    const [p, c] = await Promise.allSettled([
      costingApi.getCostProfiles(),
      costingApi.getCurrentCosts(),
    ]);
    if (p.status === 'fulfilled') setProfiles(p.value.data);
    if (c.status === 'fulfilled') setCurrentCosts(c.value.data);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRecalculate = async () => {
    setRecalcLoading(true);
    setRecalcResult(null);
    try {
      const r = await costingApi.bulkRecalculate();
      setRecalcResult(r.data);
      loadData();
    } catch (e) {
      setLocalError(e.response?.data?.error || 'Помилка перерахунку');
    }
    setRecalcLoading(false);
  };

  const handleImport = async () => {
    setImportLoading(true);
    try {
      const r = await costingApi.importProfilesFromCalcs();
      setLocalError('');
      alert(`Імпортовано: ${r.data.created} профілів. Вже існувало: ${r.data.skipped}`);
      loadData();
    } catch (e) {
      setLocalError('Помилка імпорту');
    }
    setImportLoading(false);
  };

  const openHistory = async (product) => {
    setHistoryProduct(product);
    setLoadingHistory(true);
    try {
      const r = await costingApi.getProductHistory(product.product_id);
      setHistory(r.data);
    } catch { setHistory([]); }
    setLoadingHistory(false);
    setSubTab(2);
  };

  const openEditProfile = (profile) => {
    // null/'new' = новий профіль (не null у state — інакше Dialog open={editProfile !== null} лишається закритим)
    if (profile && typeof profile === 'object') {
      setEditProfile(profile);
      setProfileForm({ ...profile });
      return;
    }
    setEditProfile('new');
    setProfileForm({
      product: '', machine: '', cavities: 1, cycle_sec: '', defect_rate: 0.02,
      sprue_weight_g: 0, sprue_return_pct: 100, machine_time_share: 1.0,
      typical_batch_size: 1000, setup_hours: 2.0, sale_price: 0, is_active: true, notes: '',
    });
  };

  const profileFieldErrors = numericFieldErrors(profileForm, PROFILE_NUM_SPECS);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      if (editProfile?.id) {
        await costingApi.updateCostProfile(editProfile.id, profileForm);
      } else {
        await costingApi.createCostProfile(profileForm);
      }
      setEditProfile(null);
      loadData();
    } catch (e) {
      const detail = e.response?.data;
      setLocalError(JSON.stringify(detail) || 'Помилка збереження');
    }
    setSaving(false);
  };

  const handleDeleteProfile = async (id) => {
    if (!window.confirm('Видалити профіль?')) return;
    await costingApi.deleteCostProfile(id);
    loadData();
  };

  const pf = (k) => (e) => setProfileForm(f => ({ ...f, [k]: e.target.value }));

  const profilesMap = Object.fromEntries(profiles.map(p => [p.product, p]));

  return (
    <Box>
      {localError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError('')}>{localError}</Alert>}

      <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Поточні собівартості" />
        <Tab label="Профілі виробів" />
        {historyProduct && <Tab label={`Динаміка: ${historyProduct.product_name}`} />}
      </Tabs>

      {/* ── ПОТОЧНІ СОБІВАРТОСТІ ── */}
      {subTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained" color="primary"
              onClick={handleRecalculate} disabled={recalcLoading}
              startIcon={recalcLoading ? <CircularProgress size={16} /> : null}
            >
              {recalcLoading ? 'Перераховую...' : '⟳ Перерахувати всі'}
            </Button>
            <Typography variant="body2" color="text.secondary">
              Оновлює ціни сировини, е/е та ін. з ERP і зберігає нові розрахунки в архів.
            </Typography>
          </Box>

          {recalcResult && (
            <Alert severity={recalcResult.errors > 0 ? 'warning' : 'success'} sx={{ mb: 2 }} onClose={() => setRecalcResult(null)}>
              Перераховано: {recalcResult.recalculated} виробів.
              {recalcResult.errors > 0 && ` Помилки (${recalcResult.errors}): ${recalcResult.error_details.map(e => `${e.product}: ${e.error}`).join('; ')}`}
            </Alert>
          )}

          {currentCosts.length === 0 ? (
            <Alert severity="info">
              Немає розрахованих собівартостей. Спочатку заповніть профілі виробів і натисніть «Перерахувати всі».
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Виріб</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Собівартість, грн/шт</TableCell>
                    <TableCell align="right">Ціна продажу (без ПДВ)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Маржа, %</TableCell>
                    <TableCell align="right">Матеріал</TableCell>
                    <TableCell align="right">Пакування</TableCell>
                    <TableCell align="right">Ел/е</TableCell>
                    <TableCell align="right">Опер.</TableCell>
                    <TableCell align="right">Накл.</TableCell>
                    <TableCell align="right">Нал-ка</TableCell>
                    <TableCell align="right">шт/год</TableCell>
                    <TableCell align="right">Дата розрах.</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentCosts.map(c => {
                    const margin = c.margin_pct;
                    const marginColor = margin == null ? 'text.secondary' : margin >= 15 ? 'success.main' : margin >= 0 ? 'warning.main' : 'error.main';
                    return (
                      <TableRow key={c.product_id} hover>
                        <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>
                          {c.product_name}
                          {c.product_article && <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.5, fontSize: 12 }}>({c.product_article})</Box>}
                          {!c.has_profile && <Chip label="без профілю" size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: 10 }} />}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700 }}>{fmt(c.unit_cost, 4)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(c.sale_price_excl_vat, 4)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: marginColor }}>
                          {margin != null ? `${fmt(margin, 1)}%` : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(c.cost_raw_material, 4)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(c.cost_packaging, 4)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(c.cost_electricity_machine, 4)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(c.cost_operator, 4)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(c.cost_overhead, 4)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(c.cost_setup, 4)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{c.good_pcs_per_hour != null ? Math.round(c.good_pcs_per_hour) : '—'}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 11, color: 'text.secondary' }}>
                          {c.calc_date ? new Date(c.calc_date).toLocaleDateString('uk-UA') : '—'}
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => openHistory(c)}>Динаміка</Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ── ПРОФІЛІ ВИРОБІВ ── */}
      {subTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openEditProfile('new')}>
              Додати профіль
            </Button>
            <Button variant="outlined" onClick={handleImport} disabled={importLoading}>
              {importLoading ? 'Імпортую...' : '↓ Імпорт з архіву розрахунків'}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              Профіль = фіксовані параметри виробництва (машина, цикл, гнізда). Ціни завжди беруться з ERP.
            </Typography>
          </Box>

          {profiles.length === 0 ? (
            <Alert severity="info">
              Профілів немає. Натисніть «Імпорт з архіву розрахунків» якщо вже є розрахунки, або «Додати профіль» вручну.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Виріб</TableCell>
                    <TableCell>ТПА</TableCell>
                    <TableCell align="right">Гнізд</TableCell>
                    <TableCell align="right">Цикл, с</TableCell>
                    <TableCell align="right">Брак, %</TableCell>
                    <TableCell align="right">Партія</TableCell>
                    <TableCell align="right">Нал-ка, год</TableCell>
                    <TableCell align="right">Ціна прод., грн</TableCell>
                    <TableCell align="center">Активний</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {profiles.map(p => (
                    <TableRow key={p.id} hover sx={{ opacity: p.is_active ? 1 : 0.5 }}>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>
                        {p.product_name}
                        {p.product_article && <Box component="span" sx={{ color: 'text.secondary', ml: 0.5, fontSize: 12 }}>({p.product_article})</Box>}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{fmtMachineLabel(p)}</TableCell>
                      <TableCell align="right">{p.cavities}</TableCell>
                      <TableCell align="right">{p.cycle_sec}</TableCell>
                      <TableCell align="right">{(p.defect_rate * 100).toFixed(1)}%</TableCell>
                      <TableCell align="right">{p.typical_batch_size}</TableCell>
                      <TableCell align="right">{p.setup_hours}</TableCell>
                      <TableCell align="right">{fmt(p.sale_price)}</TableCell>
                      <TableCell align="center">
                        <Chip label={p.is_active ? 'Так' : 'Ні'} size="small" color={p.is_active ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => openEditProfile(p)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteProfile(p.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ── ДИНАМІКА ВИРОБУ ── */}
      {subTab === 2 && historyProduct && (
        <Box>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Динаміка собівартості: {historyProduct.product_name}
          </Typography>
          {loadingHistory ? (
            <CircularProgress />
          ) : history.length === 0 ? (
            <Alert severity="info">Немає даних</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell>Дата</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Собівартість</TableCell>
                    <TableCell align="right">Маржа, %</TableCell>
                    <TableCell align="right">Матеріал</TableCell>
                    <TableCell align="right">Пакування</TableCell>
                    <TableCell align="right">Ел/е</TableCell>
                    <TableCell align="right">Опер.</TableCell>
                    <TableCell align="right">Ціна сир., грн/кг</TableCell>
                    <TableCell align="center">Активний</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...history].reverse().map(h => {
                    const marginColor = h.margin_pct == null ? 'text.secondary' : h.margin_pct >= 15 ? 'success.main' : h.margin_pct >= 0 ? 'warning.main' : 'error.main';
                    return (
                      <TableRow key={h.calc_id} hover sx={{ bgcolor: h.is_active ? 'action.selected' : 'inherit' }}>
                        <TableCell sx={{ fontSize: 12 }}>{new Date(h.date).toLocaleString('uk-UA')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: 13 }}>{fmt(h.unit_cost, 4)}</TableCell>
                        <TableCell align="right" sx={{ color: marginColor, fontWeight: 600 }}>
                          {h.margin_pct != null ? `${fmt(h.margin_pct, 1)}%` : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(h.cost_raw_material, 4)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(h.cost_packaging, 4)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(h.cost_electricity_machine, 4)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(h.cost_operator, 4)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>{fmt(h.raw_material_price_per_kg, 2)}</TableCell>
                        <TableCell align="center">
                          {h.is_active && <Chip label="Активний" size="small" color="primary" />}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ── DIALOG: РЕДАГУВАННЯ ПРОФІЛЮ ── */}
      <Dialog open={editProfile !== null} onClose={() => setEditProfile(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{editProfile?.id ? 'Редагувати профіль' : 'Новий профіль'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Виріб</InputLabel>
              <Select value={profileForm.product || ''} label="Виріб" onChange={pf('product')}>
                {(allProducts || []).map(p => (
                  <MenuItem key={p.product_id} value={p.product_id}>{p.name} {p.article && `(${p.article})`}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>ТПА</InputLabel>
              <Select value={profileForm.machine || ''} label="ТПА" onChange={pf('machine')}>
                {(machines || []).map(m => (
                  <MenuItem key={m.machine_id} value={m.machine_id}>{fmtMachineLabel(m)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Grid container spacing={2}>
              <Grid item xs={6}><TextField fullWidth size="small" label="Гнізд" type="number" value={profileForm.cavities || ''} onChange={pf('cavities')} error={!!profileFieldErrors.cavities} helperText={profileFieldErrors.cavities || ' '} inputProps={{ min: 1 }} /></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" label="Цикл, сек" type="number" value={profileForm.cycle_sec || ''} onChange={pf('cycle_sec')} error={!!profileFieldErrors.cycle_sec} helperText={profileFieldErrors.cycle_sec || ' '} inputProps={{ min: 0 }} /></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" label="Брак (0.02 = 2%)" type="number" value={profileForm.defect_rate ?? ''} onChange={pf('defect_rate')} error={!!profileFieldErrors.defect_rate} helperText={profileFieldErrors.defect_rate || ' '} inputProps={{ min: 0, max: 1 }} /></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" label="Ливник, г" type="number" value={profileForm.sprue_weight_g ?? ''} onChange={pf('sprue_weight_g')} error={!!profileFieldErrors.sprue_weight_g} helperText={profileFieldErrors.sprue_weight_g || ' '} inputProps={{ min: 0 }} /></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" label="Ливник у дробілку, %" type="number" value={profileForm.sprue_return_pct ?? ''} onChange={pf('sprue_return_pct')} error={!!profileFieldErrors.sprue_return_pct} helperText={profileFieldErrors.sprue_return_pct || ' '} inputProps={{ min: 0, max: 100 }} /></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" label="Частка машинного часу" type="number" value={profileForm.machine_time_share ?? ''} onChange={pf('machine_time_share')} error={!!profileFieldErrors.machine_time_share} helperText={profileFieldErrors.machine_time_share || ' '} inputProps={{ min: 0, max: 1 }} /></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" label="Типова партія, шт" type="number" value={profileForm.typical_batch_size || ''} onChange={pf('typical_batch_size')} error={!!profileFieldErrors.typical_batch_size} helperText={profileFieldErrors.typical_batch_size || ' '} inputProps={{ min: 1 }} /></Grid>
              <Grid item xs={6}><TextField fullWidth size="small" label="Наладка, год" type="number" value={profileForm.setup_hours ?? ''} onChange={pf('setup_hours')} error={!!profileFieldErrors.setup_hours} helperText={profileFieldErrors.setup_hours || ' '} inputProps={{ min: 0 }} /></Grid>
              <Grid item xs={12}><TextField fullWidth size="small" label="Ціна продажу (з ПДВ), грн" type="number" value={profileForm.sale_price ?? ''} onChange={pf('sale_price')} error={!!profileFieldErrors.sale_price} helperText={profileFieldErrors.sale_price || ' '} inputProps={{ min: 0 }} /></Grid>
            </Grid>
            <TextField fullWidth size="small" label="Коментар" multiline rows={2} value={profileForm.notes || ''} onChange={pf('notes')} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditProfile(null)}>Скасувати</Button>
          <Button variant="contained" onClick={handleSaveProfile} disabled={saving}>
            {saving ? 'Збереження...' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ────────────────────────────────────────────────────────────────────────────
export default function CostingPage() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [machines, setMachines] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [dyes, setDyes] = useState([]);
  const [peripherals, setPeripherals] = useState([]);
  const [packagingSchemes, setPackagingSchemes] = useState([]);
  const [packagingMaterials, setPackagingMaterials] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [factorySettings, setFactorySettings] = useState([]);
  const [calculations, setCalculations] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const ENDPOINTS = [
      { key: 'ТПА профілі', fn: costingApi.getMachineProfiles() },
      { key: 'Матеріали (доставка)', fn: costingApi.getMaterialDeliveries() },
      { key: 'Барвники (доставка)', fn: costingApi.getDyeDeliveries() },
      { key: 'Периферія', fn: costingApi.getPeripherals() },
      { key: 'Схеми пакування', fn: costingApi.getPackagingSchemes() },
      { key: 'Витрати заводу', fn: costingApi.getFactorySettings() },
      { key: 'Архів розрахунків', fn: costingApi.getCalculations() },
      { key: 'Матеріали ERP', fn: costingApi.getPackagingMaterials() },
      { key: 'Вироби ERP', fn: costingApi.getProductList() },
    ];

    const results = await Promise.allSettled(ENDPOINTS.map(e => e.fn));

    const safe = (r, def = []) => (r.status === 'fulfilled' ? r.value.data : def);
    setMachines(safe(results[0]));
    setMaterials(safe(results[1]));
    setDyes(safe(results[2]));
    setPeripherals(safe(results[3]));
    setPackagingSchemes(safe(results[4]));
    setFactorySettings(safe(results[5]));
    setCalculations(safe(results[6]));
    setPackagingMaterials(safe(results[7]));
    setAllProducts(safe(results[8]));

    const failed = results
      .map((r, i) => r.status === 'rejected' ? ENDPOINTS[i].key : null)
      .filter(Boolean);
    if (failed.length) setError(`Не вдалося завантажити: ${failed.join(', ')}`);
    else setError('');

    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Собівартість</Typography>

      {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }} variant="scrollable" scrollButtons="auto">
        <Tab label="Собівартості" icon={<CalculateIcon />} iconPosition="start" />
        <Tab label="Калькулятор" />
        <Tab label="ТПА" />
        <Tab label="Матеріали" />
        <Tab label="Периферія" />
        <Tab label="Пакування" />
        <Tab label="Витрати заводу" />
        <Tab label="Архів" icon={<ArchiveIcon />} iconPosition="start" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <StandardCostTab allProducts={allProducts} machines={machines} factorySettings={factorySettings} onRefresh={loadAll} />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <CalculatorTab
          peripherals={peripherals}
          packagingSchemes={packagingSchemes}
          factorySettings={factorySettings}
          machines={machines}
          materials={materials}
          dyes={dyes}
        />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <MachinesTab machines={machines} onRefresh={loadAll} />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <MaterialsTab materials={materials} dyes={dyes} onRefresh={loadAll} />
      </TabPanel>
      <TabPanel value={tab} index={4}>
        <PeripheralTab peripherals={peripherals} onRefresh={loadAll} />
      </TabPanel>
      <TabPanel value={tab} index={5}>
        <PackagingTab packagingMaterials={packagingMaterials} packagingSchemes={packagingSchemes} allProducts={allProducts} onRefresh={loadAll} />
      </TabPanel>
      <TabPanel value={tab} index={6}>
        <FactorySettingsTab factorySettings={factorySettings} onRefresh={loadAll} />
      </TabPanel>
      <TabPanel value={tab} index={7}>
        <ArchiveTab calculations={calculations} onRefresh={loadAll} />
      </TabPanel>
    </Box>
  );
}
